import { useEffect, useRef } from 'react';
import { Animated } from 'react-native';
import { FadeIn } from '../../../components/animations';
import { Box } from '../../../components/primitives/Box';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Text } from '../../../components/primitives/Text';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useTranslatedStageName } from '../../../hooks/useTranslatedStageName';
import { colors } from '../../../theme/colors';
import { useMetricDisplay } from '../hooks/useMetricDisplay';
import type { TimeInStageItem } from '../hooks/usePipelineAnalytics';
import { NO_DATA } from '../utils/metricDisplay';
import { isTerminalStage } from '../utils/stageRoles';

interface TimeInStageChartProps {
  data: TimeInStageItem[];
}

// No time RECORDED means no bar; a recorded time always gets one. The floor
// used to key off `> 0`, which drew nothing for a same-day transit — making a
// real measurement look exactly like a stage nobody has finished. Null is the
// only absence: it is what the API sends when there is no transit to average,
// so measured-ness is already decided upstream and this need not re-guess it
// from the value.
export function resolveBarWidth(avgDays: number | null, maxDays: number): number {
  if (avgDays === null) return 0;
  const percentage = maxDays > 0 ? (avgDays / maxDays) * 100 : 0;
  return Math.max(percentage, 5);
}

// The duration bands rank a stage from fast to slow, and two kinds of stage
// cannot be ranked on them.
//
// An UNMEASURED stage has no evidence: `days < 3` was true of the 0 that stood
// in for "no lead has finished this stage yet", so the stage with the least
// behind it was painted the funnel's best performer.
//
// A TERMINAL stage has evidence but no speed to reward. On real data `Lost` has
// a genuine 0.00-day average, which the fast band called the funnel's quickest
// stage — telling the seller their losing stage was their best performer, the
// same defect as `BEST STAGE: Lost`. How fast deals die is real information and
// stays on screen; it just does not belong on a scale built to reward speed.
export function resolveDurationColor(days: number | null, isTerminal = false): string {
  if (days === null) return colors.text.tertiary;
  if (isTerminal) return colors.status.info.main;
  if (days < 3) return colors.status.success.main;
  if (days <= 7) return colors.status.warning.main;
  return colors.status.error.main;
}

function StageBar({
  item,
  index,
  maxDays,
}: Readonly<{
  item: TimeInStageItem;
  index: number;
  maxDays: number;
}>) {
  const { t } = useTranslation();
  const { daysValue } = useMetricDisplay();
  const translateStageName = useTranslatedStageName();
  const widthAnim = useRef(new Animated.Value(0)).current;
  const barWidth = resolveBarWidth(item.avgDays, maxDays);

  useEffect(() => {
    Animated.timing(widthAnim, {
      toValue: barWidth,
      duration: 800,
      delay: index * 100,
      useNativeDriver: false,
    }).start();
  }, [barWidth, index, widthAnim]);

  const isTerminal = isTerminalStage(item);
  const barColor = resolveDurationColor(item.avgDays, isTerminal);

  return (
    <FadeIn delay={index * 80}>
      <VStack spacing="xs">
        <HStack justify="between" align="center">
          <HStack spacing="xs" align="center">
            <Text variant="body" color={colors.text.primary} style={{ fontWeight: '500' }}>
              {translateStageName(item.stageName)}
            </Text>
            {/* A closed outcome is off the speed scale entirely, so it is
                marked in words beside the stage name. Colour is never the only
                carrier of the distinction. */}
            {isTerminal && (
              <Text variant="caption" color={barColor}>
                {t('analytics.timeInStage.terminalMarker')}
              </Text>
            )}
          </HStack>
          <Text variant="caption" color={barColor} style={{ fontWeight: '600' }}>
            {daysValue(item.avgDays)}
          </Text>
        </HStack>
        <Box
          backgroundColor={colors.surface.row}
          borderRadius="md"
          borderWidth={1}
          borderColor={colors.border.subtle}
          overflow="hidden"
          style={{ height: 10 }}
        >
          <Animated.View
            style={{
              height: '100%',
              borderRadius: 6,
              backgroundColor: barColor,
              width: widthAnim.interpolate({
                inputRange: [0, 100],
                outputRange: ['0%', '100%'],
              }),
            }}
          />
        </Box>
      </VStack>
    </FadeIn>
  );
}

export function TimeInStageChart({ data }: Readonly<TimeInStageChartProps>) {
  const { t } = useTranslation();

  if (data.length === 0) {
    return (
      <Box alignItems="center" justifyContent="center" style={{ minHeight: 120 }}>
        <Text variant="body" color={colors.text.tertiary}>
          {t('analytics.timeInStage.empty')}
        </Text>
      </Box>
    );
  }

  const sorted = [...data].sort((a, b) => a.position - b.position);
  const maxDays = Math.max(...sorted.map((d) => d.avgDays ?? 0), 1);

  return (
    <VStack spacing="lg">
      {sorted.map((item, index) => (
        <StageBar key={item.stageId} item={item} index={index} maxDays={maxDays} />
      ))}
      <HStack spacing="md" justify="center" wrap>
        {[
          { label: t('analytics.timeInStage.legend.fast'), color: colors.status.success.main },
          { label: t('analytics.timeInStage.legend.medium'), color: colors.status.warning.main },
          { label: t('analytics.timeInStage.legend.slow'), color: colors.status.error.main },
          { label: t('analytics.timeInStage.legend.terminal'), color: colors.status.info.main },
          {
            label: t('analytics.timeInStage.legend.notMeasured', { dash: NO_DATA }),
            color: colors.text.tertiary,
          },
        ].map((legend) => (
          <HStack key={legend.label} spacing="xs" align="center">
            <Box
              backgroundColor={legend.color}
              borderRadius="sm"
              style={{ width: 10, height: 10 }}
            />
            <Text variant="caption" color={colors.text.tertiary}>
              {legend.label}
            </Text>
          </HStack>
        ))}
      </HStack>
    </VStack>
  );
}
